import React, { useState } from "react";
import type { HTMLWidget } from "~/types/widgets";
import { validateEmail, validatePhone } from "../../utils/validation";
import { copyToClipboard } from "../../utils/clipboard";
import TextMessage from "../ui/TextMessage";
import FormInput from "../ui/FormInput";
import CheckboxLabel from "../ui/CheckboxLabel";
import { stripInlineStyles } from "~/utils/richText";
import { trackNewsletterLead } from "~/sdk/leadTracking";

export interface Props {
  buttonText?: string;
  disclaimer?: HTMLWidget;
  /** @title Mensagem de Sucesso */
  successMessage?: string;
  /** @title Texto do Cupom */
  couponText?: string;
  /** @title Código do Cupom */
  couponCode?: string;
}

function NewsletterForm(props: Props) {
  const {
    buttonText,
    disclaimer,
    successMessage = "Cadastro realizado com sucesso!",
    couponText = "Copie este cupom e ganhe 10% OFF em sua primeira compra.",
    couponCode = "PRIMEIRACOMPRA",
  } = props;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [female, setFemale] = useState(false);
  const [male, setMale] = useState(false);
  const [preferNotToInform, setPreferNotToInform] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = async () => {
    const success = await copyToClipboard(couponCode);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!validateEmail(email)) {
      setError("Por favor, insira um email válido");
      setLoading(false);
      return;
    }

    if (!validatePhone(phone)) {
      setError("Por favor, insira um telefone válido com DDD");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/dataentities/NW/documents", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          nome: name,
          phone,
          female,
          male,
          preferNotToInform,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Não foi possível cadastrar. Tente novamente mais tarde.`,
        );
      }

      const data = await response.json();

      if (data.DocumentId) {
        void trackNewsletterLead({
          source: "newsletter_footer",
          email,
          phone,
          gender: female
            ? "female"
            : male
            ? "male"
            : preferNotToInform
            ? "undisclosed"
            : undefined,
        });
        setEmail("");
        setName("");
        setPhone("");
        setFemale(false);
        setMale(false);
        setPreferNotToInform(false);
        setSuccess(true);
      }
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Ocorreu um erro ao cadastrar",
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="formIntegration flex flex-col gap-4 text-center">
        <TextMessage additionalClasses="text-[16px] leading-[18px] mb-4">
          {successMessage}
        </TextMessage>
        <TextMessage additionalClasses="text-[14px] leading-[15px] mb-2">
          {couponText}
        </TextMessage>
        <div className="border-2 border-dashed border-[#333333] p-4 inline-block mx-auto">
          <button
            onClick={handleCopyToClipboard}
            className="font-['Poppins',Helvetica,Arial,sans-serif] not-italic font-bold text-lg leading-[20px] text-[#333333] tracking-wider bg-transparent border-none cursor-pointer hover:text-[#666666] transition-colors duration-200"
            title="Clique para copiar o cupom"
          >
            {couponCode}
          </button>
        </div>
        {copied && (
          <div className="font-['Poppins',Helvetica,Arial,sans-serif] not-italic font-normal text-xs leading-[14px] text-green-600 mt-2">
            Cupom copiado!
          </div>
        )}
      </div>
    );
  }

  return (
    <form
      className="formIntegration flex flex-col gap-4"
      onSubmit={onSubmit}
    >
      {error && (
        <div className="text-red-500 text-sm mb-2">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4">
        {/* Campos Nome, E-mail e Botão */}
        <div className="flex flex-col lg:flex-row gap-3">
          <FormInput
            name="newsletter-name"
            type="text"
            placeholder="Nome"
            value={name}
            onChange={(e) => setName(e?.currentTarget?.value)}
            required
            typeClass="type_VC"
          />
          <FormInput
            name="newsletter-email"
            type="email"
            placeholder="E-mail"
            value={email}
            onChange={(e) => setEmail(e?.currentTarget?.value)}
            required
            typeClass="type_EMAIL"
          />
          <FormInput
            name="newsletter-phone"
            type="tel"
            placeholder="Telefone"
            value={phone}
            onChange={(e) => setPhone(e?.currentTarget?.value)}
            required
            typeClass="type_TEL"
          />
          <button
            className="dinSubmit w-[315px] h-10 font-['Poppins'] not-italic font-bold text-base leading-[17px] text-white bg-[#1A1A1A] cursor-pointer lg:w-[150px] disabled:opacity-70 disabled:cursor-not-allowed"
            type="submit"
            disabled={loading}
          >
            {loading ? "Enviando..." : buttonText}
          </button>
        </div>

        {/* Checkboxes de Gênero */}
        <div className="flex flex-row gap-4 items-start lg:items-center newsletter-form-checkboxes">
          <CheckboxLabel
            name="newsletter-female"
            checked={female}
            onChange={(e) => setFemale(e?.currentTarget?.checked)}
            label="Feminino"
          />
          <CheckboxLabel
            name="newsletter-male"
            checked={male}
            onChange={(e) => setMale(e?.currentTarget?.checked)}
            label="Masculino"
          />
          <CheckboxLabel
            name="newsletter-prefer-not-to-inform"
            checked={preferNotToInform}
            onChange={(e) => setPreferNotToInform(e?.currentTarget?.checked)}
            label="Prefiro não informar"
            whiteSpaceNoWrap
          />
        </div>
        {disclaimer && (
          <span
            className="newsletter-link flex font-['Poppins'] not-italic font-normal text-[10px] leading-[145.6%] tracking-[0.085em] text-[#1A1A1A] flex-col lg:flex-row lg:gap-0.5"
            dangerouslySetInnerHTML={{ __html: stripInlineStyles(disclaimer) }}
          />
        )}
      </div>
    </form>
  );
}

export default NewsletterForm;
